import React, { useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';

import { getUtmParams } from 'src/common/links';
import { StringUtils } from 'src/common/utils/stringUtils';
import {
  DefaultLocation,
  NewSearchLocation,
} from 'src/global/models/NewSearchLocation';
import { SEOSlugUrlParams } from 'src/modules/Opportunities/pages/Explore/Components/FilterSection/context';
import { KeywordSearchField } from 'src/modules/Opportunities/pages/Explore/Components/SearchSection/KeywordSearchField';
import { LocationSearchField } from 'src/modules/Opportunities/pages/Explore/Components/SearchSection/LocationSearchField';
import { useLocationSearchValues } from 'src/modules/Opportunities/pages/Explore/Components/SearchSection/LocationSearchField/useLocationSearchKeyword';
import { SearchButton } from 'src/modules/Opportunities/pages/Explore/Components/SearchSection/SearchButton';
import * as S from 'src/modules/Opportunities/pages/Explore/Components/SearchSection/SearchSectionDesktop/SearchContainer.sc';
import { SearchRouterQueryKey } from 'src/modules/Opportunities/pages/Explore/types';

import { UpdatePastSearchesStateContext } from '../../SearchSuggestions/Context';
import { generateSearchQuery } from '../generateSearchQuery';
import { trackSearchInput } from '../GTMActions';
import { useKeywordSearchMachine } from '../useKeywordSearchMachine';

export const SearchContainer = ({ isHomepage }: { isHomepage?: boolean }) => {
  const router = useRouter();
  const query = router.query;
  const marketingURLParams = getUtmParams(query);
  const dispatch = useDispatch();
  const locationSearchFieldRef = useRef(null);

  /**
   *  @this SearchRouterQueryKey.Keyword: is used in Explore Page
   *  @this SearchRouterQueryKey.Keywords: is used in InternalSearchAndFilter Page
   *  @task https://glints.atlassian.net/browse/GM-8791
   */
  const {
    [SearchRouterQueryKey.Keyword]: keywordQuery = '',
    [SearchRouterQueryKey.Keywords]: keywordsQuery = '',
  } = query;
  const { customSlugPageKeyword } = useContext(SEOSlugUrlParams);
  const internalQueryAndFilterSearchKeyword =
    StringUtils.convertOtherCaseToTitleCase(
      StringUtils.convertQueryToSingleString(keywordsQuery)
    );

  const keywords =
    keywordQuery ||
    customSlugPageKeyword ||
    internalQueryAndFilterSearchKeyword ||
    '';

  const {
    locationInputValue,
    locationSearchValue,
    selectedCountry,
    setLocationInputValue,
    setLocationSearchValue,
  } = useLocationSearchValues();

  const [locationResponse, setLocationResponse] = useState<NewSearchLocation[]>(
    []
  );
  const [isLocationError, setIsLocationError] = useState<boolean>(false);
  const suggestionLimit = 5;
  const { updatePastJobSearches } = useContext(UpdatePastSearchesStateContext);

  const {
    currentState,
    send,
    searchKeyword,
    locationQuery,
    onKeywordChange,
    onKeywordClick,
    onLocationClick,
    onLocationErrorChange,
    onLocationResponseChange,
    onSearchEnter,
    onLocationEnter,
    onSearchButtonClick,
  } = useKeywordSearchMachine(
    locationResponse,
    locationSearchValue,
    selectedCountry,
    setLocationSearchValue,
    setIsLocationError,
    setLocationResponse,
    isLocationError
  );

  useEffect(
    function updateSearchKeyword() {
      send('UPDATE_SEARCH_KEYWORD', { searchKeyword: keywords });
    },
    [keywords, send]
  );

  const isMobile = false;

  if (currentState.matches('searchReady')) {
    const routerQuery = generateSearchQuery(
      searchKeyword,
      locationQuery,
      updatePastJobSearches,
      marketingURLParams,
      isMobile
    );
    dispatch(
      trackSearchInput({
        searchKeywords: routerQuery.query[SearchRouterQueryKey.Keyword],
        searchLocation: routerQuery.query[SearchRouterQueryKey.Location],
      })
    );
    router.push(routerQuery);

    send('RESET_TO_IDLE');
  }

  const handleSearchClick = () => {
    if (!locationSearchValue) {
      onLocationClick(DefaultLocation.getDefaultLocationAll(selectedCountry));
      if (locationSearchFieldRef.current) {
        locationSearchFieldRef.current.hideDropdown();
      }
    }
    onSearchButtonClick();
  };

  return (
    <S.Container>
      <S.FieldWrapper flex={3}>
        <KeywordSearchField
          keyword={searchKeyword}
          onChange={onKeywordChange}
          onKeywordClick={onKeywordClick}
          suggestionLimit={suggestionLimit}
          countryCode={selectedCountry}
          onEnter={onSearchEnter}
          isHomepage={isHomepage}
        />
      </S.FieldWrapper>
      <S.FieldWrapper flex={2}>
        <LocationSearchField
          ref={locationSearchFieldRef}
          selectedCountry={selectedCountry}
          locationSearchValue={locationSearchValue}
          locationInputValue={locationInputValue}
          onLocationInputChange={setLocationInputValue}
          locationResponse={locationResponse}
          onLocationClick={onLocationClick}
          isLocationError={isLocationError}
          suggestionLimit={suggestionLimit}
          onLocationErrorChange={onLocationErrorChange}
          onLocationResponseChange={onLocationResponseChange}
          onEnter={onLocationEnter}
          isHomepage={isHomepage ?? false}
        />
      </S.FieldWrapper>
      <SearchButton
        onSearchClick={handleSearchClick}
        isLocationError={isLocationError}
      />
    </S.Container>
  );
};
